import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showNotification } from 'src/redux/notification/notification.slice';
import { UsableError } from 'src/services/error-parsing/error-parsing.types';
import { useSyncAccountsCampaignsMutation } from '../endpoints/accounts.endpoints';

function syncErrorSummary(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: { error?: string } }).data;
    if (data != null && typeof data.error === 'string' && data.error.trim() !== '') {
      return data.error;
    }
  }
  if (err && typeof err === 'object' && 'message' in err && 'code' in err) {
    const u = err as UsableError;
    if (typeof u.message === 'string' && u.message.trim() !== '') {
      return u.message;
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const m = (err as { message: unknown }).message;
    if (typeof m === 'string' && m.trim() !== '') {
      return m;
    }
  }
  return 'Sync could not be started. Please try again.';
}

export function useManageAccountsSync(): {
  syncAccountsAndCampaigns: () => Promise<void>;
  isSyncAccountsCampaignsPending: boolean;
  syncSuccessModalOpen: boolean;
  onSyncSuccessModalOpenChange: (open: boolean) => void;
} {
  const dispatch = useDispatch();
  const [syncSuccessModalOpen, setSyncSuccessModalOpen] = useState(false);
  const [syncAccountsCampaigns, { isLoading: isSyncAccountsCampaignsPending }] = useSyncAccountsCampaignsMutation();

  const syncAccountsAndCampaigns = useCallback(async() => {
    try {
      const result = await syncAccountsCampaigns().unwrap();
      if (result?.status === 'success') {
        setSyncSuccessModalOpen(true);
      }
    } catch (err: unknown) {
      dispatch(showNotification({ summary: syncErrorSummary(err), theme: 'warning' }));
    }
  }, [dispatch, syncAccountsCampaigns]);

  return {
    syncAccountsAndCampaigns,
    isSyncAccountsCampaignsPending,
    syncSuccessModalOpen,
    onSyncSuccessModalOpenChange: setSyncSuccessModalOpen,
  };
}
